<template>
  <div class="welcome">
    <h2>Welcome</h2>
    <Separator />
    <p>
      Thank you for your interest in this position! Please fill in the form
      below and we look forward to receiving your application.
    </p>
  </div>

  <BaseBackground background-name="background-half-circle" fill-color="#000000">
    <BackgroundHalfCircle />
  </BaseBackground>

  <Dots v-if="response_loading" />

  <div id="seescan-applicant-form" class="applicant-form">
    <form @submit.prevent="checkForm">
      <!-- Contact Information -->
      <fieldset>
        <legend>Contact Information</legend>
        <!-- Name -->
        <label>Name<span class="asterisk">*</span></label>
        <input
          placeholder="First"
          v-model="applicant.first_name"
          :class="{ 'input-err': errors.includes('First Name required') }"
          type="text"
          @keydown.enter.prevent=""
        />
        <input
          placeholder="Last"
          v-model="applicant.last_name"
          type="text"
          :class="{ 'input-err': errors.includes('Last Name required') }"
          @keydown.enter.prevent=""
        />
        <!-- Email -->
        <label>Email<span class="asterisk">*</span></label>
        <input
          placeholder="Email"
          v-model="applicant.email"
          type="text"
          :class="{ 'input-err': errors.includes('Valid Email required') }"
          @keydown.enter.prevent=""
        />
        <input
          placeholder="Confirm"
          v-model="applicant.confirmEmail"
          type="text"
          :class="{ 'input-err': errors.includes('Emails do not match') }"
          @keydown.enter.prevent=""
        />
        <!-- Phone -->
        <label>Phone<span class="asterisk">*</span></label>
        <input
          placeholder=""
          v-model="applicant.phone"
          type="text"
          :class="{
            'input-err': errors.includes('Valid Phone Number required'),
          }"
          @keydown.enter.prevent=""
        />
      </fieldset>

      <!-- Address -->
      <fieldset>
        <legend>Address</legend>
        <!-- Street -->
        <label>Street<span class="asterisk">*</span></label>
        <input
          placeholder=""
          v-model="applicant.address"
          type="text"
          :class="{ 'input-err': errors.includes('Address required') }"
          @keydown.enter.prevent=""
        />
        <!-- City -->
        <label>City<span class="asterisk">*</span></label>
        <input
          placeholder=""
          v-model="applicant.city"
          type="text"
          :class="{ 'input-err': errors.includes('City required') }"
          @keydown.enter.prevent=""
        />
        <!-- State/Zip Code -->
        <div class="state-zip">
          <!-- State -->
          <div class="state">
            <label for="state">State<span class="asterisk">*</span></label>
            <input
              type="text"
              id="state"
              name="state"
              list="states"
              v-model="applicant.state"
              :class="{ 'input-err': errors.includes('State required') }"
              @keydown.enter.prevent=""
            />
            <datalist id="states">
              <option value="Alabama"></option>
              <option value="Alaska"></option>
              <option value="Arizona"></option>
              <option value="Arkansas"></option>
              <option value="California"></option>
              <option value="Colorado"></option>
              <option value="Connecticut"></option>
              <option value="Delaware"></option>
              <option value="District of Columbia"></option>
              <option value="Florida"></option>
              <option value="Georgia"></option>
              <option value="Hawaii"></option>
              <option value="Idaho"></option>
              <option value="Illinois"></option>
              <option value="Indiana"></option>
              <option value="Iowa"></option>
              <option value="Kansas"></option>
              <option value="Kentucky"></option>
              <option value="Louisiana"></option>
              <option value="Maine"></option>
              <option value="Maryland"></option>
              <option value="Massachusetts"></option>
              <option value="Michigan"></option>
              <option value="Minnesota"></option>
              <option value="Mississippi"></option>
              <option value="Missouri"></option>
              <option value="Montana"></option>
              <option value="Nebraska"></option>
              <option value="Nevada"></option>
              <option value="New Hampshire"></option>
              <option value="New Jersey"></option>
              <option value="New Mexico"></option>
              <option value="New York"></option>
              <option value="North Carolina"></option>
              <option value="North Dakota"></option>
              <option value="Ohio"></option>
              <option value="Oklahoma"></option>
              <option value="Oregon"></option>
              <option value="Pennsylvania"></option>
              <option value="Rhode Island"></option>
              <option value="South Carolina"></option>
              <option value="South Dakota"></option>
              <option value="Tennessee"></option>
              <option value="Texas"></option>
              <option value="Utah"></option>
              <option value="Vermont"></option>
              <option value="Virginia"></option>
              <option value="Washington"></option>
              <option value="West Virginia"></option>
              <option value="Wisconsin"></option>
              <option value="Wyoming"></option>
            </datalist>
          </div>
          <!-- Zip Code -->
          <div>
            <label>Zip<span class="asterisk">*</span></label>
            <input
              placeholder=""
              v-model="applicant.zip"
              type="text"
              :class="{
                'input-err': errors.includes('Valid Zip Code required'),
              }"
              @keydown.enter.prevent=""
            />
          </div>
        </div>
      </fieldset>

      <!-- Job Information -->
      <fieldset>
        <legend>Job Information</legend>
        <!-- Position -->
        <label>Position(s)<span class="asterisk">*</span></label>
        <p v-if="pos_loading">Loading...</p>
        <div>
          <div>
            <ul v-for="(groupPosition, name) in groupPos" :key="name">
              <fieldset id="positions">
                <legend>{{ name }}</legend>
                <li v-for="(position, idx) in groupPosition" :key="position.id">
                  <label>
                    <input
                      name="cbPositions"
                      multiple="true"
                      type="checkbox"
                      :id="idx"
                      :value="position.id"
                      v-model="posId"
                    />
                    {{ position.title }}
                  </label>
                </li>
              </fieldset>
            </ul>
          </div>
        </div>
        <p v-if="pos_error">{{ pos_error }}</p>

        <!-- Referred By -->
        <label>Referred By</label>
        <p v-if="ref_loading">Loading...</p>
        <select
          id="ref"
          v-model="applicant.ref_fk"
          :class="{ 'input-err': errors.includes('Referred By required') }"
        >
          <option value="0">- Select -</option>
          <option
            v-for="referrer in referrers"
            :key="referrer.id"
            :value="referrer.id"
          >
            {{ referrer.title }}
          </option>
        </select>

        <!-- Other -->
        <div
          class="other"
          v-show="
            (otherReferrer && applicant.ref_fk === otherReferrer.id) ||
            (employeeReferrer && applicant.ref_fk === employeeReferrer.id)
          "
        >
          <label
            >SeeScan Employee or Other<span class="asterisk">*</span></label
          >
          <input
            v-model="applicant.ref_other"
            id="ref-other"
            placeholder="Employee Name or Other"
            type="text"
            :class="{
              'input-err': errors.includes('Employee Name or Other required'),
            }"
          />
          <small>How did you learn about this position?</small>
        </div>

        <!-- Error -->
        <p v-if="ref_error">{{ ref_error }}</p>
      </fieldset>

      <!-- Salary Information-->
      <fieldset>
        <legend>Salary Range</legend>
        <!-- Start/End -->
        <div class="two-col-grid">
          <div>
            <!-- Start -->
            <label>From</label>
            <input
              placeholder="$"
              v-model="applicant.req_sal_start"
              type="text"
              :class="{
                'input-err': errors.includes('Minimum Salary required'),
              }"
              @keydown.enter.prevent=""
            />
          </div>
          <!-- End -->
          <div>
            <label>To</label>
            <input
              placeholder="$"
              v-model="applicant.req_sal_end"
              type="text"
              :class="{
                'input-err': errors.includes('Maximum Salary required'),
              }"
              @keydown.enter.prevent=""
            />
          </div>
        </div>

        <!-- Salary Type -->
        <label>Salary Type</label>
        <div class="salary-type">
          <div class="radio-group">
            <input
              v-model="applicant.req_sal_type"
              type="radio"
              id="typeAnnual"
              name="type"
              value="0"
              @keydown.enter.prevent=""
            />
            <label for="typeAnnual">Annual</label>
          </div>
          <div class="radio-group">
            <input
              v-model="applicant.req_sal_type"
              type="radio"
              id="typeHourly"
              name="type"
              value="1"
              @keydown.enter.prevent=""
            />
            <label for="typeHourly">Hourly</label>
          </div>
        </div>
      </fieldset>

      <!-- Additional Information -->
      <fieldset>
        <legend>Additional Information</legend>
        <!-- Question -->
        <label v-if="catId == 16">
          Are you an active-duty service member?<span class="asterisk">*</span>
        </label>
        <div class="activeDuty" v-if="catId == 16">
          <div class="radio-group">
            <input
              type="radio"
              id="activeDutyYes"
              name="activeDuty"
              value="1"
              v-model="applicant.active_duty"
              @keydown.enter.prevent=""
            />
            <label for="activeDutyYes">Yes</label>
          </div>
          <div class="radio-group">
            <input
              type="radio"
              id="activeDutyNo"
              name="activeDuty"
              value="0"
              v-model="applicant.active_duty"
              @keydown.enter.prevent=""
            />
            <label for="activeDutyNo">No</label>
          </div>
        </div>
        <label v-if="catId == 16 && activeDuty">
          Have you discussed SkillBridge participation with your command?<span
            class="asterisk"
            >*</span
          >
        </label>
        <div class="discussedWithCO" v-if="catId == 16 && activeDuty">
          <div class="radio-group">
            <input
              type="radio"
              id="discussedWithCOYes"
              name="discussedWithCO"
              value="1"
              v-model="applicant.discussed_with_co"
              @keydown.enter.prevent=""
            />
            <label for="discussedWithCOYes">Yes</label>
          </div>
          <div class="radio-group">
            <input
              type="radio"
              id="discussedWithCONo"
              name="discussedWithCO"
              value="0"
              v-model="applicant.discussed_with_co"
              @keydown.enter.prevent=""
            />
            <label for="discussedWithCONo">No</label>
          </div>
        </div>
        <label v-if="catId != 16">
          Do you require sponsorship to work in the United States?<span
            class="asterisk"
            >*</span
          >
        </label>
        <div class="sponsorship" v-if="catId != 16">
          <div class="radio-group">
            <input
              type="radio"
              id="sponsorshipYes"
              name="sponsorship"
              value="0"
              v-model="applicant.work_us"
              @keydown.enter.prevent=""
            />
            <label for="sponsorshipYes">Yes</label>
          </div>
          <div class="radio-group">
            <input
              type="radio"
              id="sponsorshipNo"
              name="sponsorship"
              value="1"
              v-model="applicant.work_us"
              @keydown.enter.prevent=""
            />
            <label for="sponsorshipNo">No</label>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>Privacy Policy</legend>
        <!-- Privacy Policy -->
        <p class="privacy-policy">
          <small>
            The personal information that you submit with this application will
            only be used for purposes relevant to your employment candidacy. We
            will not store your personal information for longer than is
            necessary to consider your candidacy and to comply with all
            applicable state and federal laws. We do not sell or share your
            personal information with any third parties.
          </small>
          <small>
            To learn more, please visit
            <a
              class="privacy-link"
              href="https://www.seescan.com/privacy"
              target="_blank"
              >www.seescan.com/privacy</a
            >.
          </small>
        </p>
      </fieldset>

      <!-- Upload Files -->
      <fieldset class="upload-files">
        <legend>Upload Files</legend>
        <p class="file-types">
          <small>.pdf, .txt, .doc, .docx, .rtf</small>
        </p>
        <!-- Cover Letter -->
        <div class="cover-letter">
          <p>Cover Letter<span class="asterisk">*</span></p>
          <label for="cover-letter"><span>Browse</span></label>
          <input
            id="cover-letter"
            name="cover-letter"
            type="file"
            ref="fileInputCoverLetter"
            accept=".rtf, .txt, .pdf, .doc, .docx"
            @change="onSelectFile('cover-letter')"
            :class="{ 'input-err': errors.includes('Cover Letter required') }"
          />
          <p v-if="applicant.files.cover != null" style="margin-top: -16px">
            <small> "{{ applicant.files.cover.name }}" </small>
          </p>
          <p v-else style="margin-top: -16px">
            <small>No file selected</small>
          </p>
        </div>
        <br />
        <!-- Resume -->
        <div class="resume">
          <p>Resume<span class="asterisk">*</span></p>
          <label for="resume"><span>Browse</span></label>
          <input
            id="resume"
            name="resume"
            type="file"
            ref="fileInputResume"
            accept=".rtf, .txt, .pdf, .doc, .docx"
            @change="onSelectFile('resume')"
            :class="{ 'input-err': errors.includes('Resume required') }"
          />
          <p v-if="applicant.files.resume != null" style="margin-top: -16px">
            <small> "{{ applicant.files.resume.name }}" </small>
          </p>
          <p v-else style="margin-top: -16px">
            <small>No file selected</small>
          </p>
        </div>
      </fieldset>

      <TurnstileValidate ref="tsv" @cftsjwt="captcha" />

      <!-- Errors -->
      <div v-if="errors.length" id="err-summary">
        <strong>Please correct the following error(s):</strong>
        <ul class="error">
          <li v-for="error in errors" :key="error" :value="error">
            {{ error }}
          </li>
        </ul>
      </div>

      <!-- Submit Application-->
      <div @keydown.enter.prevent="">
        <Button>Apply</Button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import BackgroundHalfCircle from '@/components/backgrounds/BackgroundHalfCircle.vue'
import BaseBackground from '@/components/global/BaseBackground.vue'
import Button from '@/components/buttons/Button.vue'
import Dots from '@/components/loaders/Dots.vue'
import Separator from '@/components/Separator.vue'
import TurnstileValidate from '@/TurnstileValidate.vue'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL
let referrerUrl = `${API_BASE_URL}/referrer`
let groupPosUrl = `${API_BASE_URL}/positions/division/seescan?groupBy=category`
const refRequest = axios.get(referrerUrl)
const groupPosRequest = axios.get(groupPosUrl)

export default {
  name: 'applicant-form',
  components: {
    BackgroundHalfCircle,
    BaseBackground,
    Button,
    Dots,
    Separator,
    TurnstileValidate,
  },
  data() {
    return {
      errorClass: 'error',
      errors: [],
      pos_loading: false,
      ref_loading: false,
      response_loading: false,
      referrers: null,
      otherReferrer: null,
      employeeReferrer: null,
      groupPos: null,
      category: '',
      pos_error: '',
      ref_error: '',
      posId: [],
      catId: '',
      from: '',
      token: null,
      applicant: {
        position_fk: 0,
        ref_fk: 0,
        ref_other: '',
        first_name: '',
        last_name: '',
        email: '',
        confirmEmail: '',
        phone: '',
        address: '',
        city: '',
        state: 'California',
        zip: '',
        req_sal_start: null,
        req_sal_end: null,
        req_sal_type: 0,
        active_duty: '',
        discussed_with_co: '',
        work_us: '',
        division: 'SeeScan',
        files: {
          cover: null,
          resume: null,
        },
      },
      submitTimeoutId: null,
    }
  },

  created: function () {
    let pId = parseInt(this.$route.params.id)
    this.posId.push(pId)
    this.applicant.position_fk = pId
    this.from =
      this.$route.params.from ||
      this.$route.query.source ||
      document.referrer ||
      ''
    var refSelect = null
    axios
      .all([refRequest, groupPosRequest])
      .then(
        axios.spread((...responses) => {
          const responseRef = responses[0].data
          const responseGroupPos = responses[1].data
          this.referrers = responseRef.data.referrers.results.filter(
            (ref) => !ref.title.toLowerCase().includes('deepsea')
          )
          this.otherReferrer = this.referrers.find(
            (ref) => ref.title.toLowerCase() === 'other'
          )
          this.employeeReferrer = this.referrers.find((ref) =>
            ref.title.toLowerCase().includes('seescan')
          )
          this.catId = this.findCategory(
            pId,
            Object.entries(responseGroupPos.data.positions.results)
          )
          this.groupPos = responseGroupPos.data.positions.results
        })
      )
      .then(() => {
        refSelect = document.getElementById('ref')
        if (this.from.length !== 0 || !this.from.includes('seescan')) {
          const options = Array.from(refSelect.options)
          options.forEach((option) => {
            let text = option.innerText || option.label || option.outerText

            if (
              this.from
                .toLowerCase()
                .includes(text.toLowerCase().replace([' search', 'the '], ''))
            ) {
              refSelect.disabled = true
              this.applicant.ref_other = this.from
              let refOther = document.getElementById('ref-other')
              refOther.disabled = true
              this.applicant.ref_fk = parseInt(option.value)
            }
          })
        }
      })
      .catch((/*errors*/) => {
        //console.error(errors)
      })
  },
  computed: {
    fromLink() {
      return this.from.length > 0
    },
    activeDuty() {
      return this.applicant.active_duty == 1
    },
  },
  methods: {
    captcha(token) {
      this.token = token.token
    },
    uploadFile(event) {
      this.files = event.target.files
    },
    findCategory(find, inn) {
      for (const room of inn) {
        const guest = room[1]
        const foundItem = guest.find((item) => item.id === find)
        if (foundItem) {
          return foundItem.categoryId
        }
      }
      return undefined
    },
    async handleSubmit() {
      var formData = new FormData()

      // append properties
      formData.append('position_fk', JSON.stringify(this.posId))
      formData.append('ref_fk', this.applicant.ref_fk)
      formData.append('ref_other', this.applicant.ref_other)
      formData.append('first_name', this.applicant.first_name)
      formData.append('last_name', this.applicant.last_name)
      formData.append('email', this.applicant.email)
      formData.append('phone', this.applicant.phone)
      formData.append('address', this.applicant.address)
      formData.append('city', this.applicant.city)
      formData.append('state', this.applicant.state)
      formData.append('zip', this.applicant.zip)
      
      formData.append(
        'req_sal_start',
        this.applicant.req_sal_start.replaceAll(',', '').replaceAll('$', '')
      )
      formData.append(
        'req_sal_end',
        this.applicant.req_sal_end.replaceAll(',', '').replaceAll('$', '')
      )
      formData.append('req_sal_type', this.applicant.req_sal_type)

      let adValue = this.catId != 16 ? 0 : this.applicant.active_duty
      formData.append('active_duty', adValue)

      let dwcoValue =
        this.catId != 16 || this.applicant.active_duty == 0
          ? 0
          : this.applicant.discussed_with_co

      formData.append('discussed_with_co', dwcoValue)
      let wusValue = this.catId == 16 ? 1 : this.applicant.work_us

      formData.append('work_us', wusValue)
      formData.append('division', this.applicant.division)
      formData.append('captchaResponse', this.token)

      // append files
      formData.append('cover', this.applicant.files.cover)
      formData.append('resume', this.applicant.files.resume)
      const submitUrl = `${API_BASE_URL}/applicant`
      await axios({
        url: submitUrl,
        method: 'POST',
        data: formData,
        timeout: 50000,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      })
        .then((response) => {
          let isSuccess = true
          let status = 0
          if (response) {
            if (Object.prototype.hasOwnProperty.call(response.data, 'obj')) {
              isSuccess = false
            }

            if (
              Object.prototype.hasOwnProperty.call(response.data, 'message')
            ) {
              if (
                Object.prototype.hasOwnProperty.call(
                  response.data.message,
                  'details'
                ) &&
                Object.prototype.hasOwnProperty.call(
                  response.data.message.details,
                  'error'
                )
              ) {
                status = response.data.message.details.errorCode
              } else {
                response.data.message.status.forEach((element) => {
                  if (element.affectedRows != 1) isSuccess = false
                })
                if (!isSuccess) status = 0
                else status = 1
              }
            }
            // redirect to response page
            this.$router.push('/response/' + status)
          }
        })
        .catch((/*err*/) => {
          //console.error(err)
          this.errors = [
            'The process timed out',
            'Your application may not have been received',
            'Please click Submit again',
          ]
        })
        .finally(() => {
          this.response_loading = false
        })
    },
    onSelectFile(fileType) {
      if (fileType === 'resume') {
        this.applicant.files.resume = this.$refs.fileInputResume.files[0]
      } else if (fileType === 'cover-letter') {
        this.applicant.files.cover = this.$refs.fileInputCoverLetter.files[0]
      }
    },
    validEmail: function (email) {
      var re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(email)
    },
    validZip: function (zip) {
      var re = /^\d{5}(?:[-\s]\d{4})?$/
      return re.test(zip)
    },
    validPhone: function (phone) {
      var re = /^[0-9]{3}?[-.]?[0-9]{3}[-.]?[0-9]{4,6}$/
      return re.test(phone)
    },
    confirmEmail: function (email, confirm) {
      if (email === confirm) {
        return true
      }
      return false
    },
    validFile: function (file) {
      const allowedTypes = [
        'text/plain', // .txt
        'application/pdf',
        'application/rtf',
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      ]

      if (!file || !allowedTypes.includes(file.type.toLowerCase())) {
        return false
      }

      const allowedExtensions = ['txt', 'rtf', 'pdf', 'doc', 'docx']

      const extension = file.name ? file.name.split('.').pop() : ''

      if (!allowedExtensions.includes(extension.toLowerCase())) {
        return false
      }

      return true
    },
    validSalary: function (start, end) {
      if (
        Number(start.replaceAll(',', '').replaceAll('$', '')) <
        Number(end.replaceAll(',', '').replaceAll('$', ''))
      ) {
        return true
      }
      return false
    },
    validReference: function (ref_fk) {
      if (parseInt(ref_fk) == 0) {
        return false
      }
      return true
    },
    validSponsor: function (spons) {
      if (this.catId != 16 && (spons == null || spons == '')) {
        return false
      }
      return true
    },
    validActiveDuty: function (active) {
      if (this.catId == 16 && (active == null || active == '')) {
        return false
      }
      return true
    },
    validDicussedWithCO: function (discussed) {
      if (
        this.catId == 16 &&
        this.applicant.active_duty == 1 &&
        (discussed == null || discussed == '')
      ) {
        return false
      }
      return true
    },
    async checkForm() {
      console.log('[checkForm] pre', this.response_loading)
      await this.$nextTick()
      if (this.response_loading) return

      console.log('[checkForm] post', this.response_loading)

      this.response_loading = true
      this.errors = []
      // Name
      if (!this.applicant.first_name) {
        this.errors.push('First Name required')
      }
      if (!this.applicant.last_name) {
        this.errors.push('Last Name required')
      }
      // Email
      if (!this.applicant.email || !this.validEmail(this.applicant.email)) {
        this.errors.push('Valid Email required')
      }
      if (
        !this.confirmEmail(this.applicant.email, this.applicant.confirmEmail)
      ) {
        this.errors.push('Emails do not match')
      }
      if (!this.applicant.confirmEmail) {
        this.errors.push('Emails do not match')
      }
      // Phone
      if (!this.applicant.phone || !this.validPhone(this.applicant.phone)) {
        this.errors.push('Valid Phone Number required')
      }
      // Address
      if (!this.applicant.address) {
        this.errors.push('Address required')
      }

      // City
      if (!this.applicant.city) {
        this.errors.push('City required')
      }
      // State
      if (!this.applicant.state) {
        this.errors.push('State required')
      } else {
        // list of states for converting names to abbv
        const stateList = {
          AL: 'Alabama',
          AK: 'Alaska',
          AZ: 'Arizona',
          AR: 'Arkansas',
          CA: 'California',
          CO: 'Colorado',
          CT: 'Connecticut',
          DE: 'Delaware',
          DC: 'District of Columbia',
          FL: 'Florida',
          GA: 'Georgia',
          HI: 'Hawaii',
          ID: 'Idaho',
          IL: 'Illinois',
          IN: 'Indiana',
          IA: 'Iowa',
          KS: 'Kansas',
          KY: 'Kentucky',
          LA: 'Louisiana',
          ME: 'Maine',
          MD: 'Maryland',
          MA: 'Massachusetts',
          MI: 'Michigan',
          MN: 'Minnesota',
          MS: 'Mississippi',
          MO: 'Missouri',
          MT: 'Montana',
          NE: 'Nebraska',
          NV: 'Nevada',
          NH: 'New Hampshire',
          NJ: 'New Jersey',
          NM: 'New Mexico',
          NY: 'New York',
          NC: 'North Carolina',
          ND: 'North Dakota',
          OH: 'Ohio',
          OK: 'Oklahoma',
          OR: 'Oregon',
          PA: 'Pennsylvania',
          RI: 'Rhode Island',
          SC: 'South Carolina',
          SD: 'South Dakota',
          TN: 'Tennessee',
          TX: 'Texas',
          UT: 'Utah',
          VT: 'Vermont',
          VA: 'Virginia',
          WA: 'Washington',
          WV: 'West Virginia',
          WI: 'Wisconsin',
          WY: 'Wyoming',
        }

        let s = 0
        for (let [key, value] of Object.entries(stateList)) {
          if (value == this.applicant.state || key == this.applicant.state) {
            s = 1
            this.applicant.state = key
          }
        }

        if (s != 1) {
          this.errors.push('Not a valid state')
        }
      }
      // Zip Code
      if (!this.applicant.zip || !this.validZip(this.applicant.zip)) {
        this.errors.push('Valid Zip Code required')
      }
      // Salary
      if (!this.applicant.req_sal_start) {
        this.errors.push('Minimum Salary required')
      }
      if (!this.applicant.req_sal_end) {
        this.errors.push('Maximum Salary required')
      }
      if (
        !this.validSalary(
          this.applicant.req_sal_start,
          this.applicant.req_sal_end
        )
      ) {
        this.errors.push('Starting salary must be less than ending')
      }

      if (!this.applicant.files.cover) {
        this.errors.push('Cover Letter required')
      }

      if (!this.validFile(this.applicant.files.cover)) {
        this.errors.push(
          'Cover Letter filetype must be one of: .pdf, .txt, .doc, .docx, .rtf'
        )
      }

      if (!this.applicant.files.resume) {
        this.errors.push('Resume required')
      }

      if (!this.validFile(this.applicant.files.resume)) {
        this.errors.push(
          'Resume filetype must be one of: .pdf, .txt, .doc, .docx, .rtf'
        )
      }

      if (!this.validSponsor(this.applicant.work_us)) {
        this.errors.push('Sponsorship inquiry required')
      }
      if (!this.validActiveDuty(this.applicant.active_duty)) {
        this.errors.push('Active Duty status required')
      }
      if (!this.validDicussedWithCO(this.applicant.discussed_with_co)) {
        this.errors.push('Must answer Commander discussion question')
      }
      if (
        !this.applicant.ref_fk ||
        !this.validReference(this.applicant.ref_fk)
      ) {
        this.errors.push('Referred By required')
      } else if (
        ((this.otherReferrer &&
          this.applicant.ref_fk === this.otherReferrer.id) ||
          (this.employeeReferrer &&
            this.applicant.ref_fk === this.employeeReferrer.id)) &&
        !this.applicant.ref_other
      ) {
        this.errors.push('Employee Name or Other required')
      }

      if (!this.token) {
        this.errors.push('Please complete the security verification')
      }

      console.log('resp_loading check: ' + this.response_loading)

      if (!this.errors.length) {
        if (this.submitTimeoutId) clearTimeout(this.submitTimeoutId)

        this.submitTimeoutId = setTimeout(() => {
          this.handleSubmit()
        }, 1000)
      } else {
        console.log('unable to submit, errors found')
        this.response_loading = false
      }
    },
  },
}
</script>

<style scoped>
/* form */
div#seescan-applicant-form {
  background-image: linear-gradient(180deg, #000000 0%, #58595b 100%);
  background-position: center center;
  background-repeat: no-repeat;
}
div#seescan-applicant-form.applicant-form form fieldset,
div#seescan-applicant-form.applicant-form form fieldset div {
  background: transparent;
  background-color: rgba(255, 255, 255, 0);
}
form span.asterisk {
  color: #5bba47;
  margin-left: 0.1em;
}
/* fieldsets */
fieldset#positions {
  border: 1px solid #aaa;
  border-radius: 16px;
  margin-top: 0;
}
fieldset#positions:hover {
  border-color: #5bba47;
}
fieldset#positions legend {
  text-align: left;
  font-weight: 600;
  font-size: smaller;
}
fieldset#positions label {
  margin: 0 0 0.5rem;
}
fieldset {
  border: none;
}
fieldset legend {
  color: #5bba47;
  font-weight: bold;
  text-align: center;
}
fieldset label {
  text-align: left;
}
fieldset input[type='text'],
fieldset select,
fieldset div.state input {
  border: 1px solid #aaa;
  border-radius: 25px;
  color: white;
  line-height: normal;
  width: 100%;
  padding: 0.75rem 1rem;
}
fieldset select,
fieldset div.state input {
  background: transparent url('../assets/icons/icon-select.svg') no-repeat
    calc(100% - 1rem) center;
  background-size: 16px 16px;
}
/* fix overlapping Chrome default icon */
fieldset div.state input::-webkit-calendar-picker-indicator {
  display: none !important;
}
fieldset select.dropdown {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
fieldset div.state input#state:active,
fieldset div.state input#state:focus,
fieldset input[type='text']:active,
fieldset input[type='text']:focus,
fieldset select:active,
fieldset select:focus {
  border: 1px solid #5bba47;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.1), 0 0 6px #5bba47;
}
/* fix to keep drop down color visible in Chrome */
fieldset select#ref option {
  color: #000000;
}
fieldset div.salary-type,
fieldset div.sponsorship,
fieldset div.salary-type,
fieldset div.sponsorship {
  flex-direction: column;
}
/* Referred By - Other */
fieldset div.other {
  text-align: left;
}
fieldset div.other small {
  margin-left: 18px;
}
fieldset div.radio-group {
  display: flex;
}
fieldset div input[type='radio'],
fieldset div input[type='checkbox'] {
  cursor: pointer;
  border: 1px solid #aaa;
  transition: 0.2s all linear;
  margin-right: 8px;
  position: relative;
}
fieldset div input[type='radio'] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  width: 16px;
  height: 16px;

  border-radius: 50%;
  top: 3px;
}
fieldset div input[type='radio']:checked {
  border: 5px solid #5bba47;
}
fieldset div.salary-type label,
fieldset div.sponsorship label {
  margin: 0 16px 0 0;
  font-weight: normal;
}
fieldset div input[type='checkbox'] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  width: 16px;
  height: 16px;

  border-radius: 10%;
  top: 4px;
}
fieldset div input[type='checkbox']:checked {
  border: 1px solid #5bba47;
  background: url('../assets/icons/icon-checkmark.svg') #5bba47 no-repeat center /
    70% auto;
}

/* Privacy START */
p.privacy-policy {
  text-align: left;
}
p small a.privacy-link {
  /* color: #5cba47; */
  color: white;
  font-size: 1em;
  transition: all 0.4s ease;
}
p small a.privacy-link:hover {
  /* color: white; */
  color: #5cba47;
  cursor: pointer;
  font-size: 1.1em;
  text-decoration: none;
} /* Privacy END */

/* Upload Files */
fieldset.upload-files {
  width: 100%;
  padding: 0 1rem;
}
p.file-types {
  padding: 0;
}
div.cover-letter {
  padding-top: 1rem;
  width: 100%;
}
div.cover-letter p,
div.resume p {
  padding-bottom: 8px;
  font-weight: 600;
}
div.cover-letter p small,
div.resume p small {
  font-weight: normal;
}
input[type='file'] {
  width: 100%;
  height: 0;
  padding: 0;
  opacity: 0;
  align-self: center;
  cursor: pointer;
}
label[for='cover-letter'],
label[for='resume'] {
  cursor: pointer;
  background-image: linear-gradient(to bottom, #5bba47 0%, #6dd400 100%);
  border-radius: 25px;
  box-sizing: border-box;
  display: block;
  position: relative;

  color: white;
  font-size: 16px;
  font-weight: normal;
  letter-spacing: 2px;
  margin: 0 auto;
  padding: 2px;

  text-transform: uppercase;
  width: fit-content;
}
label[for='cover-letter'] span,
label[for='resume'] span {
  background: #58595b;
  border-radius: 25px;

  display: flex;
  justify-content: center;

  padding: 10px 36px;

  height: 100%;
  width: 100%;

  transition: background 0.2s ease;
}

label[for='cover-letter']:hover,
label[for='resume']:hover {
  background: transparent;
}

label[for='cover-letter'] span:hover,
label[for='resume'] span:hover {
  background: #fff;
  color: black;
}

/* Errors */
div#err-summary {
  padding: 8%;
  border-radius: 24px;
  color: #fff;
  border: 1px solid #cc0000;
  background-color: #f5c6cb;
  text-align: left;
}

div#err-summary strong {
  color: #cc0000;
}

ul.error {
  list-style: none;
  color: #cc0000;
}

#seescan-applicant-form form fieldset .input-err {
  border: 1px solid #cc0000;
}
</style>
